import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  SubmitButton,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { MeetingType, PrivacyLevel, SecurityFeature } from "../api/graphql";
import { BaseSkeleton, TitleSkeleton } from "../lib/closet";
import useApolloErrorHandler from "../lib/handleApolloError";
import {
  isConfidential,
  securityDisclaimer,
  securityFeaturesDescription,
} from "../lib/securityFeaturesUtils.ts";
import { formatTime, hoursMinutesSecondsTextLabel } from "../lib/timeFormats";
import { GetCurrentInmateVoiceCallInfoDocument } from "./GetCurrentInmateVoiceCallInfo.generated";
import { StartVoiceCallDocument } from "./StartVoiceCall.generated";

type Props = {
  connectionId: string;
  privacyLevels: PrivacyLevel[] | null;
  onCancel: () => void;
};

function ReducedRemainingDurationNote({
  remainingDuration,
}: {
  remainingDuration: number;
}) {
  const { t } = useTranslation();
  const remainingMinutes = Math.floor(remainingDuration / (1000 * 60));
  return (
    <Alert severity="warning">
      {remainingDuration % (1000 * 60) > 0
        ? t(
            "You have less than {{count}} minutes available for this call, shorter than usual.",
            { count: remainingMinutes + 1 }
          )
        : t(
            "You have {{count}} minutes available for this call, shorter than usual.",
            { count: remainingMinutes }
          )}
    </Alert>
  );
}

function OutOfSessionMinutesNote({ resetLength }: { resetLength: number }) {
  const { t } = useTranslation();

  return (
    <Alert severity="error">
      {t(
        "You've used all of your voice call minutes for this session. Your session minutes reset {{count}} minutes after the end of your most recent call.",
        { count: resetLength / (60 * 1000) }
      )}
    </Alert>
  );
}

function OutOfDailyQuotaNote() {
  const { t } = useTranslation();

  return (
    <Alert severity="error">
      {t(
        "You've used all of your voice call minutes today. Your minutes reset tomorrow."
      )}
    </Alert>
  );
}

function RemainingTimeBox({
  remaining,
  resetTime,
  description,
}: {
  remaining: number | null;
  resetTime?: number | null;
  description: string;
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        height: { xs: 140, sm: 120 },
        borderRadius: 4,
        border: 0.5,
        borderColor: "divider",
        backgroundColor: grey[50],
        width: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Stack
        sx={{
          alignItems: "center",
        }}
      >
        <Typography variant="h3" style={{ textAlign: "center" }}>
          {remaining === null
            ? t("Unlimited")
            : hoursMinutesSecondsTextLabel(remaining)}
        </Typography>
        <Typography
          variant="body2"
          style={{ textAlign: "center" }}
          sx={{
            color: "text.secondary",
          }}
        >
          {description}
        </Typography>
        {resetTime && (
          <Typography
            variant="caption"
            style={{ textAlign: "center" }}
            sx={{
              color: "text.disabled",
            }}
          >
            {t("Resets at {{resetTime}}", {
              resetTime: formatTime(resetTime),
            })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function ItemNumber({ number }: { number: number }) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "50%",
        borderColor: "text.secondary",
        borderStyle: "solid",
        borderWidth: 1,
        marginTop: "1px",
        width: 24,
        height: 24,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        {number}
      </Typography>
    </Box>
  );
}

function PrivacyInformation({
  securityFeatures,
}: {
  securityFeatures: SecurityFeature[];
}) {
  const { t } = useTranslation();
  const disclaimer = securityDisclaimer(
    MeetingType.VoiceCall,
    securityFeatures
  );
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline">{t("Monitoring")}</Typography>
        <Typography variant="body1">
          {securityFeaturesDescription(MeetingType.VoiceCall, securityFeatures)}
        </Typography>
      </Box>
      {!!disclaimer && (
        <Box>
          <Typography variant="overline">{t("Privacy Notice")}</Typography>
          <Typography variant="body1">{disclaimer}</Typography>
        </Box>
      )}
    </Stack>
  );
}

function HowItWorksNote({
  number,
  title,
  description,
}: {
  number: number;
  title: string;
  description: string;
}) {
  return (
    <Grid container wrap="nowrap">
      <Grid sx={{ flexGrow: 0, marginRight: 1 }}>
        <ItemNumber number={number} />
      </Grid>
      <Grid sx={{ flexGrow: 1 }}>
        <Stack>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            {description}
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
}

function LimitedVoiceCallDialogContent({
  maxDuration,
  maxQuota,
  resetLength,
  resetTime,
  securityFeatures,
  remainingDuration,
  remainingQuota,
}: {
  securityFeatures: SecurityFeature[];
  maxDuration: number;
  maxQuota: number;
  resetLength: number;
  resetTime: number | null;
  remainingDuration: number;
  remainingQuota: number | null;
}) {
  const { t } = useTranslation();

  const showReducedRemainingDurationNote =
    remainingDuration > 0 && remainingDuration < maxDuration;
  const showOutOfSessionMinutesNote =
    remainingDuration === 0 && (remainingQuota === null || remainingQuota > 0);
  const showOutOfDailyQuotaNote = remainingQuota === 0;

  return (
    <DialogContent>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <RemainingTimeBox
            remaining={remainingDuration}
            resetTime={resetTime}
            description={t("Available now")}
          />
          <RemainingTimeBox
            remaining={remainingQuota}
            description={t("Remaining today")}
          />
        </Stack>

        <Accordion
          sx={{
            "&:before": {
              display: "none",
            },
          }}
          elevation={0}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMore color="primary" />}
            sx={{
              boxShadow: "none",
              padding: 0,
            }}
          >
            <Typography variant="body1" color="primary">
              {t("How do voice call minutes work?")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack spacing={2}>
              <HowItWorksNote
                number={1}
                title={
                  maxQuota % (60 * 60 * 1000) === 0
                    ? t("You may have {{count}} hours of calls every day", {
                        count: maxQuota / (60 * 60 * 1000),
                      })
                    : t("You may have {{count}} minutes of calls every day", {
                        count: maxQuota / (60 * 1000),
                      })
                }
                description={
                  maxQuota % (60 * 60 * 1000) === 0
                    ? t(
                        "You are entitled to have {{count}} hours ({{maxQuotaMinutes}} minutes) of free voice calls every day and can choose how to use it.",
                        {
                          count: maxQuota / (60 * 60 * 1000),
                          maxQuotaMinutes: maxQuota / (60 * 1000),
                        }
                      )
                    : t(
                        "You are entitled to have {{count}} minutes of free voice calls every day and can choose how to use it.",
                        { count: maxQuota / (60 * 1000) }
                      )
                }
              />
              <HowItWorksNote
                number={2}
                title={t("A single call can be up to {{count}} minutes long", {
                  count: maxDuration / (60 * 1000),
                })}
                description={t(
                  "If your call is less than {{count}} minutes, you may make additional calls with the remaining time you have available now.",
                  { count: maxDuration / (60 * 1000) }
                )}
              />
              {maxQuota > maxDuration && (
                <HowItWorksNote
                  number={3}
                  title={t("You must take a break to use all of your time")}
                  description={t(
                    "You can access more of your daily limit for calls after a {{count}} minute break.",
                    { count: resetLength / (60 * 1000) }
                  )}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <PrivacyInformation securityFeatures={securityFeatures} />
      </Stack>
      <Box
        sx={{
          mt: 2,
        }}
      >
        {showReducedRemainingDurationNote && (
          <ReducedRemainingDurationNote remainingDuration={remainingDuration} />
        )}
        {showOutOfSessionMinutesNote && (
          <OutOfSessionMinutesNote resetLength={resetLength} />
        )}
        {showOutOfDailyQuotaNote && <OutOfDailyQuotaNote />}
      </Box>
    </DialogContent>
  );
}

function UnlimitedVoiceCallDialogContent({
  securityFeatures,
}: {
  securityFeatures: SecurityFeature[];
}) {
  return (
    <DialogContent>
      <PrivacyInformation securityFeatures={securityFeatures} />
    </DialogContent>
  );
}

export default function BeginVoiceCallDialog({
  connectionId,
  privacyLevels,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const handleApolloError = useApolloErrorHandler();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, error } = useQuery(GetCurrentInmateVoiceCallInfoDocument, {
    fetchPolicy: "network-only",
    variables: { connectionId },
  });

  const [startCall, { loading: starting }] = useMutation(
    StartVoiceCallDocument,
    {
      onError: (e) => handleApolloError(e),
      onCompleted: (startedData) => {
        if (startedData.createVoiceCall.meeting.call?.id) {
          navigate(
            `/voice-call/${startedData.createVoiceCall.meeting.call.id}`,
            {
              state: { from: location.pathname },
            }
          );
        }
      },
      variables: {
        input: {
          connectionId,
        },
      },
    }
  );

  if (error) throw error;
  if (!data?.currentInmate)
    return (
      <Dialog fullWidth title="">
        <Stack
          spacing={4}
          sx={{
            margin: 4,
          }}
        >
          <TitleSkeleton />
          <BaseSkeleton variant="rectangular" height={300} />{" "}
        </Stack>
      </Dialog>
    );

  const limitedByQuota = !data.connection.organizationMembership;
  // this mirrors the logic in createVoiceCall
  const securityFeatures = isConfidential(data.connection.security)
    ? data.connection.security
    : privacyLevels?.includes(PrivacyLevel.UnmonitoredLogged)
      ? [SecurityFeature.Logged]
      : [
          SecurityFeature.Monitoring,
          SecurityFeature.Recording,
          SecurityFeature.Logged,
        ];

  const remainingDuration =
    data.connection.voiceCallAvailability.remainingTimeQuota === null // null equals unlimited
      ? data.connection.voiceCallAvailability.remainingDuration
      : Math.min(
          data.connection.voiceCallAvailability.remainingTimeQuota,
          data.connection.voiceCallAvailability.remainingDuration
        );

  return (
    <Dialog
      title={t("Review the following before making your voice call")}
      onClose={onCancel}
    >
      {limitedByQuota ? (
        <LimitedVoiceCallDialogContent
          securityFeatures={securityFeatures}
          maxDuration={data.currentInmate.facility.maxVoiceCallDuration || 0}
          maxQuota={data.currentInmate.voiceCallTimeQuota}
          resetLength={data.currentInmate.facility.voiceCallResetTime}
          resetTime={data.currentInmate.remainingVoiceCallDurationResetTime}
          remainingQuota={
            data.connection.voiceCallAvailability.remainingTimeQuota
          }
          remainingDuration={remainingDuration}
        />
      ) : (
        <UnlimitedVoiceCallDialogContent securityFeatures={securityFeatures} />
      )}
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button autoFocus onClick={onCancel}>
          {t("Cancel")}
        </Button>

        <SubmitButton
          submitting={starting}
          onClick={() => startCall()}
          disabled={limitedByQuota && remainingDuration === 0}
        >
          {t("Agree & Call")}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
